
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertTriangle, Clock, MapPin, HelpCircle } from 'lucide-react';
import { useDealerReport } from '@/hooks/dealers/useDealerReport';
import { REPORT_TYPE_LABELS } from '@/types/dealerReport';
import { useAuth } from '@/hooks/useAuth';

interface DealerReportModalProps {
  dealerId: string;
  dealerName: string;
  dealerAddress?: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const reportTypeIcons = {
  'CLOSED_PERMANENTLY': Clock,
  'NOT_A_DEALER': AlertTriangle,
  'WRONG_ADDRESS': MapPin,
  'OTHER': HelpCircle,
};

export const DealerReportModal: React.FC<DealerReportModalProps> = ({
  dealerId,
  dealerName,
  dealerAddress,
  isOpen,
  onOpenChange,
}) => {
  const { isAuthenticated } = useAuth();
  const { form, onSubmit, isSubmitting } = useDealerReport({
    dealerId,
    onSuccess: () => onOpenChange(false),
  });
  
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      form.reset();
    }
  };

  const selectedReportType = form.watch('report_type');
  const description = form.watch('description') || '';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <DialogContent 
        className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto p-0 bg-white [&>button]:text-gray-700 [&>button]:hover:text-gray-900 [&>button]:opacity-100"
        onPointerDownOutside={(e) => {
          // Previeni completamente la chiusura su click esterno
          e.preventDefault();
          e.stopPropagation();
        }}
        onEscapeKeyDown={(e) => {
          // Permetti la chiusura con ESC
          handleOpenChange(false);
        }}
        onInteractOutside={(e) => {
          // Previeni qualsiasi interazione esterna
          e.preventDefault();
          e.stopPropagation();
        }}
        aria-labelledby="dealer-report-title"
        aria-describedby="dealer-report-description"
      >
        {/* Header semplificato senza pulsante X duplicato */}
        <DialogHeader className="px-6 py-5 border-b border-gray-100">
          <DialogTitle 
            id="dealer-report-title"
            className="text-xl font-semibold text-gray-900"
          >
            Segnala Compro Oro
          </DialogTitle>
          <p id="dealer-report-description" className="text-sm text-gray-600 mt-1">
            Aiutaci a mantenere aggiornate le informazioni sui commercianti
          </p>
        </DialogHeader>

        {/* Contenuto principale */}
        <div className="px-6 py-5">
          {/* Informazioni dealer con migliore contrasto */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {dealerName}
            </h3>
            {dealerAddress && (
              <p className="text-sm text-gray-700 flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                {dealerAddress}
              </p>
            )}
          </div>
        
          {!isAuthenticated ? (
            <div className="py-6 text-center">
              <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <p className="text-gray-900 font-medium mb-2">Accesso richiesto</p>
              <p className="text-gray-600 mb-6">Devi effettuare l'accesso per poter inviare una segnalazione.</p>
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                className="px-6"
              >
                Chiudi
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSubmit(e);
                }} 
                className="space-y-6"
                onClick={(e) => e.stopPropagation()}
              >
                <FormField
                  control={form.control}
                  name="report_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-gray-900 mb-3 block">
                        Tipo di segnalazione <span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <div className="space-y-3">
                          {Object.entries(REPORT_TYPE_LABELS).map(([key, label]) => {
                            const IconComponent = reportTypeIcons[key as keyof typeof reportTypeIcons] || AlertTriangle;
                            const isSelected = field.value === key;
                            
                            return (
                              <div
                                key={key}
                                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                  isSelected 
                                    ? 'border-blue-500 bg-blue-50 shadow-sm' 
                                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-25'
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  field.onChange(key);
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                role="radio"
                                aria-checked={isSelected}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    field.onChange(key);
                                  }
                                }}
                              >
                                <IconComponent 
                                  className={`h-5 w-5 mr-3 flex-shrink-0 ${
                                    isSelected ? 'text-blue-600' : 'text-gray-500'
                                  }`} 
                                />
                                <span className={`text-sm font-medium ${
                                  isSelected ? 'text-blue-900' : 'text-gray-800'
                                }`}>
                                  {label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-600 font-medium" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-gray-900 mb-2 block">
                        Descrizione (opzionale)
                      </FormLabel>
                      <FormControl>
                        <div className="space-y-3">
                          <Textarea
                            placeholder="Fornisci dettagli aggiuntivi sulla segnalazione..."
                            className="min-h-[120px] resize-none border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder-gray-500"
                            maxLength={500}
                            {...field}
                            value={field.value ?? ''}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              field.onChange(e);
                            }}
                          />
                          <div className="flex justify-end">
                            <span className="text-sm text-gray-600 font-medium">
                              {description.length}/500 caratteri
                            </span>
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-600 font-medium" />
                    </FormItem>
                  )}
                />

                {/* Box informazioni importanti con migliore contrasto */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
                  <div className="flex items-start">
                    <AlertTriangle className="h-6 w-6 text-blue-600 mt-0.5 mr-4 flex-shrink-0" />
                    <div>
                      <h4 className="text-base font-semibold text-blue-900 mb-3">
                        Informazioni importanti:
                      </h4>
                      <ul className="text-sm text-blue-800 space-y-2 font-medium">
                        <li>• La segnalazione verrà esaminata dal nostro team</li>
                        <li>• Riceverai una notifica sullo stato della segnalazione</li>
                        <li>• Le segnalazioni false possono comportare la sospensione dell'account</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Pulsanti integrati nel form */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleOpenChange(false);
                    }} 
                    disabled={isSubmitting}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="px-6 font-medium"
                  >
                    Annulla
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting || !selectedReportType}
                    className="min-w-[140px] bg-red-600 hover:bg-red-700 text-white px-6 font-medium shadow-sm"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Invia Segnalazione
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
